import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useCreateSprintMutation } from '../store/sprintApi';

interface StartSprintProps {
  isOpen: boolean;
  onClose: () => void;
}

const StartSprint: React.FC<StartSprintProps> = ({ isOpen, onClose }) => {
  const [sprintName, setSprintName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [createSprint, { isLoading }] = useCreateSprintMutation();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSprintName('');
      setSelectedFile(null);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
      } else {
        toast.error('Only PDF files are allowed.');
        setSelectedFile(null);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sprintName.trim()) return toast.error('Sprint name is required.');
    if (!selectedFile) return toast.error('Please upload a PDF file.');

    const formData = new FormData();
    formData.append('sprintName', sprintName);
    formData.append('pdfFile', selectedFile);

    try {
      await createSprint(formData).unwrap();
      toast.success(`Sprint "${sprintName}" created successfully!`);
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to create sprint');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const content = (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all duration-300 ${
          isAnimating ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-4'
        }`}
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-trident-gray-100">
          <div>
            <h2 className="font-display font-bold text-xl text-trident-gray-900">Start New Sprint</h2>
            <p className="text-sm text-trident-gray-500 mt-0.5">
              Upload a PDF brief. The AI will extract blog topics and assign them to contributors.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-trident-gray-400 hover:text-trident-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div>
            <label htmlFor="sprintName" className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">
              Sprint Name
            </label>
            <input
              type="text"
              id="sprintName"
              value={sprintName}
              onChange={(e) => setSprintName(e.target.value)}
              className="input-field"
              placeholder="e.g., Sprint 5 - Q4 Content Push"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">
              Upload Sprint Brief (PDF)
            </label>
            <div
              {...getRootProps()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-200 p-8 text-center
                ${isDragActive ? 'border-trident-orange bg-trident-orange-light' : 'border-trident-gray-200 hover:border-trident-orange/50 hover:bg-trident-gray-50'}`}
            >
              <input {...getInputProps()} disabled={isLoading} />
              <div className="w-14 h-14 rounded-full bg-trident-gradient/10 flex items-center justify-center mx-auto mb-3">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              {selectedFile ? (
                <div>
                  <div className="font-display font-bold text-trident-gray-900 mb-1">{selectedFile.name}</div>
                  <div className="text-xs text-trident-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB · Click or drop to replace</div>
                </div>
              ) : isDragActive ? (
                <p className="text-trident-orange font-display font-semibold">Drop your PDF here…</p>
              ) : (
                <>
                  <p className="font-display font-semibold text-trident-gray-700 mb-1">Drag & drop your PDF here</p>
                  <p className="text-xs text-trident-gray-500">or click to browse</p>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-display font-semibold text-trident-gray-700 bg-white border border-trident-gray-200 rounded-lg hover:bg-trident-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary inline-flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                  Creating Sprint...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Create Sprint
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default StartSprint;
